import React from "react";
import { Box, Typography, Select, Option, Stack } from "@mui/joy";
import { Building2, Plus, X, Ruler, Palette, Hash } from "lucide-react";
import { Controller, useFormContext, useFieldArray } from "react-hook-form";
import { FormInput, Button, IconButton } from "../../../shared";

const CHIMNEY_TYPE_OPTIONS = [
    { value: "Brick", label: "Brick/Stone" },
    { value: "Siding", label: "Siding" },
] as const;

const selectBaseSx = {
    bgcolor: "background.surface",
    borderRadius: "4px",
    boxShadow: "none",
    width: "100%",
} as const;

export function Chimneys({ loading }: { loading?: boolean }) {
    const { control } = useFormContext();
    const { fields, append, remove } = useFieldArray({
        control,
        name: "Chimneys",
    });

    return (
        <Box>
            <Box mb={2}>
                <Typography level="title-md">Chimney</Typography>
                <Typography level="body-sm" color="neutral">
                    Specify chimney types, sizes, colors, and counts.
                </Typography>
            </Box>

            <Box
                sx={{
                    p: 2,
                    borderRadius: "4px",
                    border: "1px solid",
                    borderColor: "rgba(128, 128, 128, 0.2)",
                    bgcolor: "rgba(128, 128, 128, 0.05)",
                    backdropFilter: "blur(12px)",
                }}
            >
                <Typography
                    level="body-xs"
                    sx={{
                        textTransform: "uppercase",
                        fontWeight: "xl",
                        letterSpacing: "0.5px",
                    }}
                >
                    Chimney List
                </Typography>
                <Typography
                    level="body-xs"
                    color="neutral"
                    sx={{ mt: -0.5, mb: 1.5, fontSize: "12px" }}
                >
                    Add one or more chimneys.
                </Typography>

                <Stack spacing={1.5}>
                    {fields.map((item, index) => (
                        <Box
                            key={item.id}
                            sx={{
                                display: "flex",
                                gap: 1,
                                alignItems: "flex-start",
                                width: "100%",
                                minWidth: 0,
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    gap: 0,
                                    alignItems: "flex-start",
                                    flex: 1,
                                    minWidth: 0,
                                }}
                            >
                                <Box
                                    sx={{
                                        width: { xs: 120, sm: 160 },
                                        flexShrink: 0,
                                    }}
                                >
                                    <Controller
                                        name={`Chimneys.${index}.Type`}
                                        control={control}
                                        render={({ field }) => (
                                            <Select
                                                {...field}
                                                size="md"
                                                disabled={loading}
                                                onChange={(_, newValue) =>
                                                    field.onChange(newValue)
                                                }
                                                placeholder="Type"
                                                startDecorator={
                                                    <Building2 size={16} />
                                                }
                                                sx={{
                                                    ...selectBaseSx,
                                                    borderTopRightRadius: 0,
                                                    borderBottomRightRadius: 0,
                                                    borderRight: 0,
                                                }}
                                            >
                                                {CHIMNEY_TYPE_OPTIONS.map(
                                                    (option) => (
                                                        <Option
                                                            key={option.value}
                                                            value={option.value}
                                                        >
                                                            {option.label}
                                                        </Option>
                                                    ),
                                                )}
                                            </Select>
                                        )}
                                    />
                                </Box>

                                <Box
                                    sx={{
                                        width: { xs: 140, sm: 220 },
                                        flexShrink: 0,
                                        minWidth: 0,
                                    }}
                                >
                                    <Controller
                                        name={`Chimneys.${index}.Size`}
                                        control={control}
                                        render={({ field }) => (
                                            <Select
                                                {...field}
                                                size="md"
                                                disabled={loading}
                                                onChange={(_, newValue) =>
                                                    field.onChange(newValue)
                                                }
                                                placeholder="Size"
                                                startDecorator={
                                                    <Ruler size={16} />
                                                }
                                                sx={{
                                                    ...selectBaseSx,
                                                    borderRadius: 0,
                                                    borderLeft: 0,
                                                    borderRight: 0,
                                                }}
                                            >
                                                <Option value="None">
                                                    None
                                                </Option>
                                                <Option value='Small (24"x24")'>
                                                    Small (24"×24")
                                                </Option>
                                                <Option value='Average (32"x36")'>
                                                    Average (32"×36")
                                                </Option>
                                                <Option value='Large (32"x60")'>
                                                    Large (32"×60")
                                                </Option>
                                            </Select>
                                        )}
                                    />
                                </Box>

                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <FormInput
                                        name={`Chimneys.${index}.Color`}
                                        placeholder="Color"
                                        disabled={loading}
                                        startDecorator={<Palette size={16} />}
                                        sx={{
                                            borderRadius: 0,
                                            borderLeft: 0,
                                            borderRight: 0,
                                            minWidth: 0,
                                        }}
                                    />
                                </Box>

                                <Box sx={{ width: 100, flexShrink: 0 }}>
                                    <FormInput
                                        name={`Chimneys.${index}.Count`}
                                        type="number"
                                        placeholder="Qty"
                                        disabled={loading}
                                        startDecorator={<Hash size={16} />}
                                        sx={{
                                            borderTopLeftRadius: 0,
                                            borderBottomLeftRadius: 0,
                                            borderLeft: 0,
                                            minWidth: 0,
                                        }}
                                    />
                                </Box>
                            </Box>

                            <IconButton
                                size="sm"
                                variant="plain"
                                color="danger"
                                disabled={loading}
                                onClick={() => remove(index)}
                                sx={{ mt: 0.5 }}
                            >
                                <X size={20} />
                            </IconButton>
                        </Box>
                    ))}

                    <Button
                        size="sm"
                        variant="outlined"
                        color="neutral"
                        startDecorator={<Plus size={16} />}
                        onClick={() =>
                            append({
                                Type: "Brick",
                                Size: 'Average (32"x36")',
                                Color: "",
                                Count: 1,
                            })
                        }
                        disabled={loading}
                        sx={{ alignSelf: "flex-start", mt: 0.5 }}
                    >
                        Add Chimney
                    </Button>
                </Stack>
            </Box>
        </Box>
    );
}
